/**
 * Hadir shared constants — 04 §9.1.
 *
 * These are THE names for these numbers. No app redefines them under another
 * name. The API's env variables default to these values; an env value that
 * differs is a deliberate operator override (04 §9.1).
 */
/* ─────────────────────────── QR token (02 §2) ─────────────────────────── */
/** Rotation period of the displayed QR token, in ms. */
export const QR_ROTATE_MS = 5000;
/** How long after `iat` a token is still accepted, in ms. */
export const QR_GRACE_MS = 10000;
/** Tolerance for a token whose `iat` is in the future (replica clock skew), in ms. */
export const QR_FUTURE_SKEW_MS = 2000;
/**
 * Lifetime of a **claim ticket**, in ms — the second half of the scan-and-pick
 * flow (M2).
 *
 * {@link QR_GRACE_MS} is 10 s because a photographed code forwarded out of the
 * hall must be dead almost immediately. But 10 s is the budget for *fetching*
 * the roster, not for finishing with it: a student then has to find their name
 * among up to 300, tap it, sometimes type the last four digits of their
 * university id, and sometimes wait on an indoor GPS fix. Enforcing the QR
 * grace on the claim as well meant honest students doing everything right were
 * told `TOKEN_EXPIRED`.
 *
 * So `GET /public/session` keeps the strict 10 s check — **a genuinely fresh
 * code is still the only way to see a roster at all**, which is the property
 * that kills a forwarded screenshot — and on success it mints a ticket that
 * `POST /public/claim` accepts for this long instead.
 *
 * What this deliberately widens: a student can fetch a roster, walk out of the
 * hall, and hand the ticket to someone else within these two minutes. That is
 * real, but it is far smaller than the hole it replaces (a flow that rejected
 * most honest claims), it is bounded by this TTL, and the one-device-one-name
 * rule (`UNIQUE(section_id, device_hash)`) still applies to whoever uses it.
 * Keep the number short for exactly that reason.
 *
 * The API reads it from `CLAIM_TICKET_TTL_MS` (same default); it is named here
 * so the student app can render the countdown without guessing.
 */
export const CLAIM_TICKET_TTL_MS = 120000;
/**
 * Path of the public, login-free scan page — `${STUDENT_APP_URL}/s?t=<token>`.
 *
 * Deliberately one character: the string is rendered as a QR every 5 s, and a
 * shorter URL means a lower-density code that a phone at the back of a hall
 * still resolves. The older authenticated flow lives at `/scan`; `parseQr`
 * accepts both so printed material and old builds keep working.
 */
export const PUBLIC_SCAN_PATH = '/s';
/**
 * How many trailing characters of a `university_id` stay visible in `idHint`,
 * and therefore how many the student types when `identity_check` is on.
 */
export const IDENTITY_CONFIRM_LEN = 4;
/* ─────────────────────────── Geofence (02 §3) ─────────────────────────── */
/** Default section radius in metres. */
export const DEFAULT_RADIUS_M = 50;
/** Reject a fix whose reported accuracy is worse than this, in metres. */
export const MAX_ACCURACY_M = 100;
/** Reject a fix older than this (`clientTime - fixAt`), in ms. */
export const GEO_MAX_FIX_AGE_MS = 30000;
/** Smallest radius a section may use, in metres (02 D7). */
export const MIN_RADIUS_M = 20;
/** Largest radius any section may use, in metres (02 D7). */
export const MAX_RADIUS_M = 300;
/** Largest radius a TA may set without ADMIN, in metres (02 D7). */
export const TA_MAX_RADIUS_M = 100;
/* ───────────────────────── Section lifecycle (09 §1) ──────────────────── */
/** Minutes after `openedAt` at which the PRESENT phase becomes LATE. */
export const LATE_AFTER_MIN = 15;
/** Minutes after `openedAt` at which the section auto-closes. */
export const CLOSE_AFTER_MIN = 30;
/** Ceiling for `closeAfterMin` on extend/reopen, in minutes (09 §1.3). */
export const MAX_CLOSE_AFTER_MIN = 180;
/** How long after `closedAt` a TA may reopen a section, in minutes (02 D8). */
export const REOPEN_GRACE_MIN = 15;
/** Length of a mid-lecture checkpoint window, in ms (02 §6.3). */
export const CHECKPOINT_WINDOW_MS = 180000;
/* ───────────────────────── Device binding (02 §4) ─────────────────────── */
/** ACTIVE devices a student may hold at once. */
export const MAX_ACTIVE_DEVICES = 2;
/** An ACTIVE device unseen for this many days may be auto-replaced (02 D4). */
export const DEVICE_STALE_DAYS = 30;
/** New device rows a student may create per 24 h (02 D13). */
export const DEVICE_MAX_NEW_PER_DAY = 2;
/* ───────────────────────────── Transport ──────────────────────────────── */
/** REST base path. Health endpoints live OUTSIDE this prefix. */
export const API_PREFIX = '/api/v1';
/** Socket.IO namespace the TA dashboard connects to (04 §6). */
export const SOCKET_NAMESPACE = '/ta';
/** Room every dashboard watching one section joins. */
export function sectionRoom(sectionId) {
    return `section:${sectionId}`;
}
/** Room that carries course-wide events such as `device.pending` (04 §6.3). */
export function courseRoom(courseId) {
    return `course:${courseId}`;
}
/* ─────────────────────────────── Cookies ─────────────────────────────── */
/** Staff refresh cookie — `Path=/api/v1/auth; SameSite=Strict` (04 §9.5). */
export const COOKIE_STAFF_REFRESH = 'hadir_rt';
/** Student refresh cookie — `SameSite=Lax` for iOS standalone PWAs (02 D6). */
export const COOKIE_STUDENT_REFRESH = 'hadir_srt';
/** Student device mirror — `Path=/api/v1/attendance`, 400 days (02 §4.2). */
export const COOKIE_INSTALLATION_ID = 'hadir_iid';
export const COOKIE_NAMES = {
    staffRefresh: COOKIE_STAFF_REFRESH,
    studentRefresh: COOKIE_STUDENT_REFRESH,
    installationId: COOKIE_INSTALLATION_ID,
};
/* ──────────────────────── Rate limit defaults (04 §9.4) ───────────────── */
/**
 * Boot defaults only. The per-IP scan numbers are runtime settings
 * (`settings.rate_limits`, ADMIN-editable and audited); the API reads them
 * from Redis every 5 s and falls back to these.
 */
export const RATE_LIMIT_DEFAULTS = {
    /** Accepted + rejected scan requests per IP per minute. */
    scanIpPerMin: 600,
    /** Rejected scan attempts (from token verification onward) per IP per minute. */
    scanIpRejectedPerMin: 60,
    /** Ceiling for IPs inside `settings.campus_cidrs`. */
    scanIpCampusPerMin: 2000,
    /** Scan requests per student per minute (a LOW_ACCURACY rejection refunds one). */
    scanStudentPerMin: 10,
    /**
     * `POST /public/claim` per IP per minute (M2).
     *
     * Read this together with `scanIpCampusPerMin`: a whole hall shares one
     * campus NAT address (02 D1), so 20/min would lock out a lecture if it were
     * the only ceiling. It is the number for an IP the API does NOT recognise —
     * an IP inside `settings.campus_cidrs` must be limited by
     * `scanIpCampusPerMin` instead. Getting that wrong turns a rate limiter into
     * an outage for every student after the first twenty.
     */
    publicClaimIpPerMin: 20,
    /** `GET /public/session` per IP per minute (M2) — same campus-CIDR caveat. */
    publicSessionIpPerMin: 120,
    /** Login attempts per IP+identifier per minute. */
    loginIpIdentifierPerMin: 5,
    /** Failed logins per IP per minute. */
    loginIpFailedPerMin: 60,
    /** Refresh calls per IP per minute. */
    refreshIpPerMin: 200,
    /** Student imports per user per minute. */
    importUserPerMin: 5,
    /** Export requests per user per minute. */
    exportUserPerMin: 10,
    /** Spot-checks per user per minute. */
    spotCheckUserPerMin: 10,
    /** Everything else, per IP per minute. */
    defaultIpPerMin: 300,
};
/* ────────────────────────────── Pagination ───────────────────────────── */
/** Default page size of every cursor-paginated list endpoint (04 §4). */
export const DEFAULT_PAGE_SIZE = 50;
/** Maximum page size of every cursor-paginated list endpoint (04 §4). */
export const MAX_PAGE_SIZE = 100;
/* ─────────────────────────────── Misc ────────────────────────────────── */
/** Display/export time zone. The database always stores UTC. */
export const APP_TZ = 'Africa/Cairo';
/** Default `university_id` shape; the API may override it with STUDENT_ID_PATTERN. */
export const STUDENT_ID_PATTERN = '^[A-Za-z0-9\\-\\/]{4,20}$';
/** Risk-score bands used by every risk marker in the UIs (02 §3.5). */
export const RISK_BANDS = { none: 0, low: 1, medium: 3, high: 6 };
//# sourceMappingURL=constants.js.map